<template>
  <div class="form-patient-contacts">
    <input-typed-phone
      v-for="({ type, value }, index) in value.phoneList"
      v-bind:key="index"
      title=""
      v-bind:violations="phonesInputErrors[index]"
      v-bind:value="value"
      v-bind:type="type"
      v-on:invalid="onInvalid"
      v-on:input="onInputPhone(index, $event)"
    >
      <button
        class="form-patient-contacts__icon"
        v-if="canInitiateCall"
        type="button"
        v-on:click="onInitiateCall(value)" />
      <button
        v-if="index === 0 && phoneNumberCount < $options.maxPhoneNumberCount"
        type="button"
        class="form-patient-contacts-add"
        v-on:click="onAddPhone">
        + Телефон
      </button>
      <button
        v-if="index > 0"
        type="button"
        class="form-patient-contacts-remove"
        v-on:click="onRemovePhone">
        - Удалить
      </button>
    </input-typed-phone>
    <input-email
      class="form-patient-contacts__input"
      label=""
      placeholder="E-mail"
      v-bind:required="false"
      v-bind:value="value.email"
      v-bind:violations="emailInputErrors"
      v-on:input="onInputEmail"
    />
    <input-checkbox
      title="Согласен на получение SMS"
      v-bind:value="value.isAllowedSms"
      v-on:input="onInputIsAllowedSms"
    />
    <input-checkbox
      title="Не звонить"
      v-bind:value="value.isNoCalls"
      v-on:input="onInputIsNoCalls"
    />
  </div>
</template>

<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputEmail from '@/components/InputEmail.vue';
import InputTypedPhone, { PHONE_TYPE_MOBILE } from '@/components/InputTypedPhone.vue';

/**
 * @description Контакты пациента.
 * @param {string}  email
 * @param {boolean} isAllowedSms
 * @param {boolean} isNoCalls
 * @param {any[]}   phoneList
 * @constructor
 */
export function PatientContacts(
  email = '',
  isAllowedSms = false,
  isNoCalls = false,
  phoneList = [],
) {
  this.email = email;
  this.isAllowedSms = isAllowedSms;
  this.isNoCalls = isNoCalls;
  this.phoneList = phoneList.length === 0 ? [{ type: PHONE_TYPE_MOBILE, value: '' }] : phoneList;

  this.setEmail = (value) => new this.constructor(value, isAllowedSms, isNoCalls, phoneList);
  this.setIsAllowedSms = (value) => new this.constructor(email, value, isNoCalls, phoneList);
  this.setIsNoCalls = (value) => new this.constructor(email, isAllowedSms, value, phoneList);
  this.setPhoneList = (value) => new this.constructor(email, isAllowedSms, isNoCalls, value);

  this.toString = () => JSON.stringify({
    email,
    isAllowedSms,
    isNoCalls,
    phoneList: this.phoneList.map(({ type, value }) => ({ type, value })),
  });
}

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события инициации звонка.
 * @type {string}
 */
const NAME_EVENT_INITIATE_CALL = 'initiate-call';

/**
 * @description Максимальное количество телефонов у пациента.
 * @type {number}
 */
const MAX_PHONE_NUMBER_COUNT = 2;

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Пустой список нарушения ограничений телефонных номеров.
 * @type {Array}
 */
const EMPTY_PHONE_VIOLATIONS = [];
/**
 * @description Компонент формы контактов пациента.
 * @type {Vue}
 */
export default {
  maxPhoneNumberCount: MAX_PHONE_NUMBER_COUNT,
  components: {
    InputCheckbox,
    InputEmail,
    InputTypedPhone,
  },
  computed: {
    emailInputErrors: resolveInputErrors('email'),
    phonesInputErrors() {
      const numbers = [];
      for (let i = 0; i < this.phoneNumberCount; i += 1) {
        const cause = `phone-list.${i}`;
        const causes = cause in this.violationCauses
          ? this.violationCauses[cause]
          : EMPTY_PHONE_VIOLATIONS;
        numbers.push(causes);
      }
      return numbers;
    },
    /**
     * @description Количество телефонных номеров.
     * @type {number}
     */
    phoneNumberCount() {
      return this.value.phoneList.length;
    },
  },
  methods: {
    /**
     * @description Обработать добавление телефона.
     * @returns {void}
     */
    onAddPhone() {
      if (this.phoneNumberCount < MAX_PHONE_NUMBER_COUNT) {
        const phoneList = this.value.phoneList.slice();
        phoneList.push({ value: '', type: PHONE_TYPE_MOBILE });
        this.$emit(NAME_EVENT_INPUT, this.value.setPhoneList(phoneList));
      }
    },
    /**
     * @description Обработать ввод электронного адреса.
     * @param {string} value
     * @returns {void}
     */
    onInputEmail(value) {
      this.$emit(NAME_EVENT_INPUT, this.value.setEmail(value));
    },
    /**
     * @description Обработать ввод согласия на получения SMS.
     * @param {boolean} value
     * @returns {void}
     */
    onInputIsAllowedSms(value) {
      this.$emit(NAME_EVENT_INPUT, this.value.setIsAllowedSms(value));
    },
    /**
     * @description Обработать ввод не звонить.
     * @param {boolean} value
     * @returns {void}
     */
    onInputIsNoCalls(value) {
      this.$emit(NAME_EVENT_INPUT, this.value.setIsNoCalls(value));
    },
    /**
     * @description Обработать ввод телефона.
     * @param {number} index
     * @param {any}    newValue
     * @returns {void}
     */
    onInputPhone(index, newValue) {
      const updatePhones = (candidate, candidateIndex) => (
        index === candidateIndex ? newValue : candidate
      );
      const phoneList = this.value.phoneList.map(updatePhones);
      this.$emit(NAME_EVENT_INPUT, this.value.setPhoneList(phoneList));
    },
    /**
     * @description Обработать некорректность поля.
     * @returns {void}
     */
    onInvalid() {
      this.$emit('invalid');
    },
    /**
     * @description Обработать инициацию звонка.
     * @param {string} phoneNumber
     * @returns {void}
     */
    onInitiateCall(phoneNumber) {
      this.$emit(NAME_EVENT_INITIATE_CALL, phoneNumber);
    },
    /**
     * @description Обработать удаление телефона.
     * @returns {void}
     */
    onRemovePhone() {
      if (this.phoneNumberCount > 1) {
        const phoneList = this.value.phoneList.slice(0, 1);
        this.$emit(NAME_EVENT_INPUT, this.value.setPhoneList(phoneList));
      }
    },
  },
  name: 'FormPatientContacts',
  props: {
    /**
     * @description Может инициировать звонки.
     */
    canInitiateCall: { default: () => false, type: Boolean },
    /**
     * @description Значение.
     * @type {PatientContacts}
     */
    value: { required: true, type: PatientContacts },
    /**
     * @description Нарушения ограничений.
     * @type {Object}
     */
    violationCauses: { required: true, type: Object },
  },
};
</script>
<style scoped lang="scss">
.form-patient-contacts {
  flex-direction: column;
  display: flex;
  gap: 27px;
  position: relative;
  &__icon {
    background-image: url('@/assets/img/phone-blue.svg');
    background-size: contain;
    background-position: center;
    width: 20px;
    height: 20px;
  }
  &__input {
    max-width: 242px;
    width: 100%;
  }
  &-add,
  &-remove {
    font-weight: 400;
    font-size: 16px;
    color: var(--action-color);
    padding: 7px 0;
    cursor: pointer;
  }
  &-remove {
    color: var(--warning-color);
  }
}

@media screen and (max-width: 480px) {
  .form-patient-contacts {
    &__input {
      max-width: 100%;
    }
  }
}
</style>
